import { useState, useEffect } from 'react';
import appConfig from '@/config/env';

const IPIFY_API_URL = 'https://api.ipify.org?format=json';

const fetchUserPublicIP = async (): Promise<string | null> => {
    try {
        const response = await fetch(IPIFY_API_URL);
        if (!response.ok) {
            return null;
        }
        const data = await response.json();
        return data.ip;
    } catch {
        return null;
    }
};

type IPCheckState = 'loading' | 'allowed' | 'denied';

/**
 * Hook to check if the user's IP address is allowed to access OTP features.
 * Returns the current state of the IP check: 'loading', 'allowed', or 'denied'.
 * If no IP restrictions are configured (OTP_ALLOWED_IPS is empty or undefined),
 * the check will immediately resolve to 'allowed'.
 */
export const useOTPIPCheck = (): IPCheckState => {
    const [ipCheckState, setIpCheckState] = useState<IPCheckState>('loading');

    useEffect(() => {
        const checkIP = async (): Promise<void> => {
            const allowedIPs = appConfig.OTP_ALLOWED_IPS;

            // If no IP restriction is configured, allow access
            if (!allowedIPs || allowedIPs.length === 0) {
                setIpCheckState('allowed');
                return;
            }

            const userIP = await fetchUserPublicIP();

            if (userIP && allowedIPs.includes(userIP)) {
                setIpCheckState('allowed');
            } else {
                setIpCheckState('denied');
            }
        };

        checkIP();
    }, []);

    return ipCheckState;
};

